<template>
  <ContentWrap>
    <el-form :inline="true" :model="query" class="-mb-15px" label-width="68px">
      <el-form-item label="用户姓名">
        <el-input v-model="query.userNickname" clearable placeholder="昵称" class="!w-160px" />
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="query.status" clearable placeholder="全部" class="!w-160px">
          <el-option label="申请中" :value="0" />
          <el-option label="已通过" :value="1" />
          <el-option label="已驳回" :value="2" />
          <el-option label="已打款" :value="3" />
        </el-select>
      </el-form-item>
      <el-form-item label="方式">
        <el-select v-model="query.type" clearable placeholder="全部" class="!w-160px">
          <el-option label="微信" :value="1" />
          <el-option label="支付宝" :value="2" />
          <el-option label="银行卡" :value="3" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery"><Icon icon="ep:search" class="mr-5px" />搜索</el-button>
        <el-button @click="resetQuery"><Icon icon="ep:refresh" class="mr-5px" />重置</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list" stripe>
      <el-table-column label="编号" prop="id" width="80" />
      <el-table-column label="用户" min-width="140">
        <template #default="{ row }">
          <div>ID：{{ row.userId }}</div>
          <div>昵称：{{ row.userNickname || '-' }}</div>
        </template>
      </el-table-column>
      <el-table-column label="金额(元)" min-width="100">
        <template #default="{ row }">{{ fen2yuan(row.price) }}</template>
      </el-table-column>
      <el-table-column label="方式" width="90">
        <template #default="{ row }">{{ typeText(row.type) }}</template>
      </el-table-column>
      <el-table-column label="收款账号" prop="accountInfo" min-width="140" show-overflow-tooltip />
      <el-table-column label="状态" width="100">
        <template #default="{ row }">
          <el-tag :type="statusTag(row.status)">{{ statusText(row.status) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="备注" prop="auditRemark" min-width="140" show-overflow-tooltip />
      <el-table-column label="申请时间" prop="createTime" min-width="170" :formatter="dateFormatter" />
      <el-table-column label="操作" width="160" fixed="right">
        <template #default="{ row }">
          <el-button
            v-if="row.status === 0"
            link
            type="warning"
            v-hasPermi="['bosscircle:dist-withdraw:audit']"
            @click="openAudit(row)"
          >审核</el-button>
          <el-button
            v-if="row.status === 1"
            link
            type="primary"
            v-hasPermi="['bosscircle:dist-withdraw:audit']"
            @click="onPayout(row)"
          >确认打款</el-button>
          <el-button v-if="row.status !== 0 && row.status !== 1" link type="primary" @click="openAudit(row, true)"
            >详情</el-button
          >
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="query.pageNo"
      v-model:limit="query.pageSize"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <Dialog v-model="auditVisible" :title="audit.readonly ? '提现详情' : '提现审核'" width="640px">
    <el-descriptions :column="2" border class="mb-16px" label-width="96px">
      <el-descriptions-item label="单号">{{ audit.row?.id }}</el-descriptions-item>
      <el-descriptions-item label="状态">{{ statusText(audit.row?.status ?? -1) }}</el-descriptions-item>
      <el-descriptions-item label="用户" :span="2">
        {{ audit.row?.userNickname || '-' }}（ID {{ audit.row?.userId }}）
      </el-descriptions-item>
      <el-descriptions-item label="金额">¥{{ fen2yuan(audit.row?.price) }}</el-descriptions-item>
      <el-descriptions-item label="方式">{{ typeText(audit.row?.type ?? 0) }}</el-descriptions-item>
      <el-descriptions-item label="收款账号" :span="2">{{
        audit.row?.accountInfo || '-'
      }}</el-descriptions-item>
      <el-descriptions-item label="申请时间" :span="2">{{
        audit.row?.createTime || '-'
      }}</el-descriptions-item>
      <el-descriptions-item label="备注" :span="2">{{
        audit.row?.auditRemark || '-'
      }}</el-descriptions-item>
    </el-descriptions>
    <template v-if="!audit.readonly && audit.row?.status === 0">
      <el-divider content-position="left">审核操作</el-divider>
      <el-form label-width="96px">
        <el-form-item label="审核结果">
          <el-radio-group v-model="audit.pass">
            <el-radio :value="true">通过</el-radio>
            <el-radio :value="false">驳回</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="!audit.pass" label="驳回原因">
          <el-input
            v-model="audit.remark"
            type="textarea"
            :rows="3"
            maxlength="200"
            show-word-limit
            placeholder="可选"
          />
        </el-form-item>
      </el-form>
    </template>
    <template #footer>
      <el-button @click="auditVisible = false">{{ audit.readonly ? '关闭' : '取消' }}</el-button>
      <el-button
        v-if="!audit.readonly && audit.row?.status === 0"
        type="primary"
        :loading="audit.saving"
        @click="submitAudit"
        >提交审核</el-button
      >
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { dateFormatter } from '@/utils/formatTime'
import * as Api from '@/api/business/distWithdraw'

defineOptions({ name: 'BusinessDistWithdraw' })

const message = useMessage()
const loading = ref(false)
const list = ref<Api.DistWithdrawVO[]>([])
const total = ref(0)
const query = reactive({
  pageNo: 1,
  pageSize: 10,
  userNickname: undefined as string | undefined,
  status: undefined as number | undefined,
  type: undefined as number | undefined
})

const fen2yuan = (fen?: number) => ((Number(fen || 0) || 0) / 100).toFixed(2)
const typeText = (t: number) => (t === 1 ? '微信' : t === 2 ? '支付宝' : t === 3 ? '银行卡' : '-')
const statusText = (s: number) =>
  s === 0 ? '申请中' : s === 1 ? '已通过' : s === 2 ? '已驳回' : s === 3 ? '已打款' : '-'
const statusTag = (s: number) =>
  s === 0 ? 'warning' : s === 1 ? 'success' : s === 2 ? 'danger' : s === 3 ? 'info' : ''

const getList = async () => {
  loading.value = true
  try {
    const data = await Api.getDistWithdrawPage({
      ...query,
      userNickname: query.userNickname?.trim() || undefined
    })
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}
const handleQuery = () => {
  query.pageNo = 1
  getList()
}
const resetQuery = () => {
  query.userNickname = undefined
  query.status = undefined
  query.type = undefined
  handleQuery()
}

const auditVisible = ref(false)
const audit = reactive({
  row: undefined as Api.DistWithdrawVO | undefined,
  pass: true,
  remark: '',
  saving: false,
  readonly: false
})

const openAudit = (row: Api.DistWithdrawVO, readonly = false) => {
  audit.row = row
  audit.pass = true
  audit.remark = ''
  audit.readonly = readonly || row.status !== 0
  auditVisible.value = true
}

const submitAudit = async () => {
  if (!audit.row?.id) return
  audit.saving = true
  try {
    if (audit.pass) {
      await Api.approveDistWithdraw(audit.row.id)
      message.success('已通过')
    } else {
      await Api.rejectDistWithdraw({ id: audit.row.id, auditRemark: audit.remark })
      message.success('已驳回，余额已退回')
    }
    auditVisible.value = false
    getList()
  } finally {
    audit.saving = false
  }
}

const onPayout = async (row: Api.DistWithdrawVO) => {
  await message.confirm(
    `确认已线下打款 ¥${fen2yuan(row.price)} 至 ${typeText(row.type)} ${row.accountInfo || ''}？`
  )
  await Api.payoutDistWithdraw(row.id)
  message.success('已标记打款')
  getList()
}

onMounted(getList)
</script>
